import API from '../apiCreate'
import type { LeadState } from '../types/crm/crm.types'
import type { EmptyResponse } from '../types/base.types'
import type {
  CrmBulkRemoveLeadFunnelsResponse,
  CrmAddLeadFunnelResponse,
  CrmDeleteLeadDealResponse,
  CrmLeadCardResponse,
  CrmLeadCardInFunnelResponse,
  CrmUpdateLeadCardInFunnelResponse,
} from '../types/crm/crmFunnels.types'
import type {
  CrmCreateLeadFunnelTaskRequest,
  CrmCreateLeadFunnelTaskResponse,
  CrmLeadTasksResponse,
  CrmLeadTasksStatus,
  CrmGetLeadFunnelTasksParams,
} from '../types/crm/crmLeadTasks.types'

/** Связки лид↔воронка / карточка сделки: `/v1/crm-lead-funnel/*`. */
export const crmLeadFunnelApi = {
  /** POST /v1/crm-lead-funnel/add-funnel?leadId= — добавить сделку (воронку) лиду. */
  addFunnelToLead: (params: {
    lead_id: number
    funnel_id: number
    manager_id?: number | null
    deal_id?: number | null
    status_id?: number | null
    comment?: string | null
    traffic_channel_id?: number | null
  }) =>
    API.post<CrmAddLeadFunnelResponse>(
      `crm-lead-funnel/add-funnel?leadId=${params.lead_id}`,
      {
        funnel_id: params.funnel_id,
        ...(params.manager_id !== undefined
          ? { manager_id: params.manager_id }
          : {}),
        ...(params.deal_id != null ? { deal_id: params.deal_id } : {}),
        ...(params.status_id != null ? { status_id: params.status_id } : {}),
        ...(params.comment != null && params.comment !== ''
          ? { comment: params.comment }
          : {}),
        ...(params.traffic_channel_id != null
          ? { traffic_channel_id: params.traffic_channel_id }
          : {}),
      },
    ),
  bulkAddFunnelToLeads: (params: {
    lead_ids: number[]
    funnel_id: number
    manager_id?: number | null
  }) =>
    API.post<EmptyResponse>('crm-lead-funnel/bulk-add', {
      lead_ids: params.lead_ids,
      funnel_id: params.funnel_id,
      manager_id: params.manager_id ?? null,
    }),
  bulkRemoveFunnelFromLeads: (params: {
    lead_ids: number[]
    funnel_id: number
  }) =>
    API.post<CrmBulkRemoveLeadFunnelsResponse>('crm-lead-funnel/bulk-remove', {
      lead_ids: params.lead_ids,
      funnel_id: params.funnel_id,
    }),
  /** DELETE /v1/crm-lead-funnel/delete-deal?lead_funnel_id= — soft-delete сделки. */
  deleteDeal: (leadFunnelId: number) =>
    API.delete<CrmDeleteLeadDealResponse>(
      `crm-lead-funnel/delete-deal?lead_funnel_id=${leadFunnelId}`,
    ),
  getLeadCardInFunnel: (params: {
    lead_id: number
    funnel_id: number
    lead_funnel_id?: number
    tasks_status?: CrmLeadTasksStatus
    tasks_limit?: number
    tasks_cursor?: number | string
  }) => {
    const search = new URLSearchParams()
    search.set('leadId', String(params.lead_id))
    search.set('funnelId', String(params.funnel_id))
    if (
      params.lead_funnel_id != null &&
      Number.isFinite(params.lead_funnel_id) &&
      params.lead_funnel_id > 0
    ) {
      search.set('lead_funnel_id', String(params.lead_funnel_id))
    }
    if (params.tasks_status) {
      search.set('tasks_status', params.tasks_status)
    }
    if (params.tasks_limit != null && Number.isFinite(params.tasks_limit)) {
      search.set('tasks_limit', String(params.tasks_limit))
    }
    if (params.tasks_cursor != null && String(params.tasks_cursor) !== '') {
      search.set('tasks_cursor', String(params.tasks_cursor))
    }
    return API.get<CrmLeadCardInFunnelResponse>(
      `crm-lead-funnel/card-funnel?${search.toString()}`,
    )
  },
  /**
   * POST /v1/crm-lead-funnel/create-task — задача TT на `lead_funnel_id`.
   * `manager_id` = `user.id` (не `crm_managers.id`).
   */
  createLeadFunnelTask: (data: CrmCreateLeadFunnelTaskRequest) =>
    API.post<CrmCreateLeadFunnelTaskResponse>(
      'crm-lead-funnel/create-task',
      data,
    ),
  /**
   * GET /v1/task-tracker/tasks/reference-items?system_key=crm_lead&value={leadFunnelId}
   * Список задач по lead_funnel_id (тот же shape, что `data.tasks` в card-funnel).
   */
  getLeadFunnelTasks: (params: CrmGetLeadFunnelTasksParams) => {
    const search = new URLSearchParams()
    search.set('system_key', 'crm_lead')
    search.set('value', String(params.leadFunnelId))
    search.set('status', params.status ?? 'all')
    if (params.limit != null && Number.isFinite(params.limit)) {
      search.set('limit', String(params.limit))
    }
    if (params.cursor != null && String(params.cursor) !== '') {
      search.set('cursor', String(params.cursor))
    }
    return API.get<CrmLeadTasksResponse>(
      `task-tracker/tasks/reference-items?${search.toString()}`,
    )
  },
  getLeadCard: (params: { lead_id: number }) =>
    API.get<CrmLeadCardResponse>(
      `crm-lead-funnel/card?leadId=${params.lead_id}`,
    ),
  // Для массовой отправки лидов в воронку "в работе", "обработанные" или "клиенты". Или для массовой смены менеджера лидов.
  changeLeadFunnelsState: (
    params: { type: LeadState; manager_id?: number },
    data: { lead_funnel_ids: number[] },
  ) =>
    API.post<EmptyResponse>(
      `crm-lead-funnel/change-lead-funnels-state?type=${params.type}${
        params.manager_id !== undefined
          ? `&manager_id=${params.manager_id}`
          : ''
      }`,
      data,
    ),
  updateLeadCardInFunnel: (
    params: {
      lead_id: number
      funnel_id: number
      lead_funnel_id?: number
    },
    data: {
      current_status_id?: number
      manager_id?: number | null
      next_contact_at?: string | null
      is_primary?: boolean
      traffic_channel_id?: number | null
      comment?: string | null
      utm?: {
        source?: string | null
        medium?: string | null
        campaign?: string | null
        content?: string | null
        term?: string | null
        utm_source?: string | null
        utm_medium?: string | null
        utm_campaign?: string | null
        utm_content?: string | null
        utm_term?: string | null
      } | null
    },
  ) =>
    API.patch<CrmUpdateLeadCardInFunnelResponse>(
      `crm-lead-funnel/update-card-funnel?leadId=${params.lead_id}&funnelId=${
        params.funnel_id
      }${
        params.lead_funnel_id != null &&
        Number.isFinite(params.lead_funnel_id) &&
        params.lead_funnel_id > 0
          ? `&lead_funnel_id=${params.lead_funnel_id}`
          : ''
      }`,
      data,
    ),
}
